import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import privateAxios from "../utils/axiosPrivate";
import { showToast } from "../utils/utils";
import { showToastType } from "../utils/constants/appConstants";
import { commonErrMsg, promoCode } from "../locales/en";
import {
  createPromoCodeURL,
  getDelatePromocodePlatformURL,
  getPromocodeCsvDownloadUrl,
  getPromocodePlatformURL,
  getPromocodeUsageDetailsUrl,
  getReferalEmailpromocodeURL
} from "../utils/constants/apiEndPoints";

const initialState = {
  loading: false,
  loadingReferralEmailUsers: false,
  referralUsers: null
};

// THUNK apply
const promocodeSlice = createSlice({
  name: "promocode",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createPromocodethunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPromocodethunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPromocodethunk.rejected, (state, action) => {
        state.loading = false;
        const error = action.payload;

        if (error?.response) {
          const { status, data } = error.response;

          if (status === 409) {
            const errorMessage = data?.message || commonErrMsg.item4;
            showToast(errorMessage, showToastType.error);
          } else if (status === 500) {
            const errors = data?.errors || [commonErrMsg.item4];
            showToast(
              Array.isArray(errors) ? errors.join(", ") : errors,
              showToastType.error
            );
          } else {
            const { message, errors: responseErrors } = data || {};
            let errorMessage = message || commonErrMsg.item4;

            if (Array.isArray(responseErrors) && responseErrors.length > 0) {
              errorMessage = `${errorMessage}: ${responseErrors.join(", ")}`;
            }

            showToast(errorMessage, showToastType.error);
          }
        } else {
          showToast(commonErrMsg.item2, showToastType.error);
        }
      })
      // === Fetch All Promo Codes ===
      .addCase(fetchPromocodesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPromocodesThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchPromocodesThunk.rejected, (state, action) => {
        state.loading = false;

        const error = action.payload;

        if (error?.response) {
          const { status, data } = error.response;
          const errors = data?.errors || [];

          if (status === 500) {
            showToast(errors, showToastType.error);
          }
        } else {
          showToast(commonErrMsg.item2, showToastType.error);
        }
      })
      //delete
      .addCase(deletePromocodeThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePromocodeThunk.fulfilled, (state) => {
        state.loading = false;
        showToast(promoCode?.table?.errMsg?.item3, showToastType.success);
      })
      .addCase(deletePromocodeThunk.rejected, (state, action) => {
        state.loading = false;
        const error = action.payload;

        const errorMessage =
          error?.response?.data?.message ||
          promoCode?.table?.errMsg?.item4 ||
          commonErrMsg.item4;

        showToast(errorMessage, showToastType.error);
      })
      // === Fetch Referral Email PromoCode Users ===
      .addCase(fetchReferralEmailUsersThunk.pending, (state) => {
        state.loadingReferralEmailUsers = true;
      })
      .addCase(fetchReferralEmailUsersThunk.fulfilled, (state, action) => {
        state.loadingReferralEmailUsers = false;

        state.referralUsers = action.payload;
      })
      .addCase(fetchReferralEmailUsersThunk.rejected, (state, action) => {
        state.loadingReferralEmailUsers = false;
        const error = action.payload;

        const errorMessage =
          error?.response?.data?.errors?.[0] ||
          error?.response?.data?.message ||
          error?.message ||
          commonErrMsg.item2;

        showToast(errorMessage, showToastType.error);
      });
  }
});

//fetch data
export const fetchPromocodesThunk = createAsyncThunk(
  "promocode/fetchPromocodes",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getPromocodePlatformURL, {
        params: payload
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPromocodeUsageDetailsThunk = createAsyncThunk(
  "promocode/getPromocodeUsageDetails",
  async ({ code, page, size, sortBy, order }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(
        getPromocodeUsageDetailsUrl(code),
        { params: { page, size, sortBy, order } }
      );
      return {
        rows: response.data?.data || [],
        totalPages: response.data?.pageInfo?.totalPages || 0,
        totalElements: response.data?.pageInfo?.totalElements || 0
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getPromocodeCsvFile = createAsyncThunk(
  "promocode/getPromocodeCsvFile",
  async (promoCode, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(
        getPromocodeCsvDownloadUrl(promoCode),
        {
          responseType: "blob"
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePromocodeThunk = createAsyncThunk(
  "promocode/deletePromocode",
  async (id, { rejectWithValue }) => {
    try {
      const response = await privateAxios.delete(
        getDelatePromocodePlatformURL(id)
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchReferralEmailUsersThunk = createAsyncThunk(
  "promocode/fetchReferralEmailUsers",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getReferalEmailpromocodeURL, {
        params: payload
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
// New thunk for creating promo code
export const createPromocodethunk = createAsyncThunk(
  "promocode/createPromocode",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.post(createPromoCodeURL, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export default promocodeSlice.reducer;
